import { toolCallStore, type ChatCardStage } from "@/shared/ui/chat-card/tool-call-store";
import {
  chatCardClient,
  chatCardSeed,
  isRecord,
  type ChatCardClient,
  type ChatCardSeed,
} from "@/features/portfolio-analysis/lib/chat-card-client";

export type ChatProposalSeed = ChatCardSeed;

export type ChatProposalClient = ChatCardClient;

export interface ChatProposalPortfolio {
  id: string;
  name: string;
}

export interface ChatProposal {
  toolCallId: string;
  seed: ChatProposalSeed;
  client: ChatProposalClient | null;
  portfolio: ChatProposalPortfolio | null;
  handedOver: boolean;
  analysis: ProposalAnalysis | null;
  handedOverAt: number | null;
}

export type ProposalAnalysis = "pending" | "started" | "optimising" | "ready" | "dropped";

export function openChatProposal(toolCallId: string, seed: ChatProposalSeed): void {
  STORE.open({
    toolCallId,
    seed,
    client: null,
    portfolio: null,
    handedOver: false,
    analysis: null,
    handedOverAt: null,
  });
}

export function chatProposalAnswered(toolCallId: string): void {
  STORE.answered(toolCallId);
}

export function hasChatProposal(toolCallId: string): boolean {
  return STORE.has(toolCallId);
}

export function useChatProposal(toolCallId: string): ChatProposal | null {
  return STORE.useEntry(toolCallId);
}

export function useChatProposalAnswering(toolCallId: string): boolean {
  return STORE.useAnswering(toolCallId);
}

export function useChatProposalStages(): (toolCallId: string) => ChatCardStage | null {
  return STORE.useStages();
}

export function chooseChatProposalClient(toolCallId: string, client: ChatProposalClient): void {
  STORE.update(toolCallId, (entry) =>
    entry.handedOver ? entry : { ...entry, client, portfolio: null },
  );
}

export function chooseChatProposalPortfolio(
  toolCallId: string,
  portfolio: ChatProposalPortfolio,
): void {
  STORE.update(toolCallId, (entry) => (entry.handedOver ? entry : { ...entry, portfolio }));
}

export function handOverChatProposal(
  toolCallId: string,
  answer: { client: ChatProposalClient; portfolio: ChatProposalPortfolio },
): boolean {
  if (STORE.read(toolCallId)?.handedOver !== false) return false;
  STORE.update(toolCallId, (entry) => ({
    ...entry,
    ...answer,
    handedOver: true,
    analysis: "pending",
    handedOverAt: Date.now(),
  }));
  return true;
}

export function usePendingProposalAnalysis(portfolioId: string): string | null {
  const entries = STORE.useAll();
  return (
    entries.find((entry) => analysisAwaited(entry) && entry.portfolio?.id === portfolioId)
      ?.toolCallId ?? null
  );
}

export function analysisAwaited(entry: ChatProposal): boolean {
  return (
    entry.analysis === "pending" &&
    entry.handedOverAt !== null &&
    Date.now() - entry.handedOverAt < HAND_OVER_WINDOW_MS
  );
}

export function startProposalAnalysis(toolCallId: string): boolean {
  if (STORE.read(toolCallId)?.analysis !== "pending") return false;
  STORE.update(toolCallId, (entry) => ({ ...entry, analysis: "started" }));
  return true;
}

export function noteProposalAnalysis(
  portfolioId: string,
  phase: Extract<ProposalAnalysis, "started" | "optimising" | "ready">,
): void {
  for (const entry of STORE.all()) {
    if (entry.portfolio?.id !== portfolioId || entry.analysis === phase) continue;
    if (!FOLLOWED.has(entry.analysis)) continue;
    STORE.update(entry.toolCallId, (known) => ({ ...known, analysis: phase }));
  }
}

export function holdProposalAnalysis(toolCallId: string): () => void {
  HOLDS.set(toolCallId, (HOLDS.get(toolCallId) ?? 0) + 1);
  return () => {
    HOLDS.set(toolCallId, (HOLDS.get(toolCallId) ?? 1) - 1);
    window.setTimeout(() => {
      if ((HOLDS.get(toolCallId) ?? 0) === 0) dropProposalAnalysis(toolCallId);
    }, 0);
  };
}

export function dropProposalAnalysis(toolCallId: string): void {
  if (STORE.read(toolCallId)?.analysis !== "pending") return;
  STORE.update(toolCallId, (entry) => ({ ...entry, analysis: "dropped" }));
}

function narrowEntry(value: unknown): ChatProposal | null {
  if (!isRecord(value) || typeof value.toolCallId !== "string") return null;
  const client = chatCardClient(value.client);
  const portfolio = portfolioOf(value.portfolio);
  const handedOver = value.handedOver === true && client !== null && portfolio !== null;
  return {
    toolCallId: value.toolCallId,
    seed: chatCardSeed(value.seed),
    client,
    portfolio: client === null ? null : portfolio,
    handedOver,
    analysis: handedOver ? analysisOf(value.analysis) : null,
    handedOverAt: handedOver && typeof value.handedOverAt === "number" ? value.handedOverAt : null,
  };
}

function analysisOf(value: unknown): ProposalAnalysis {
  return value === "pending" || value === "started" || value === "optimising" || value === "ready"
    ? value
    : "dropped";
}

function portfolioOf(value: unknown): ChatProposalPortfolio | null {
  if (!isRecord(value)) return null;
  const { id, name } = value;
  if (typeof id !== "string" || id === "" || typeof name !== "string") return null;
  return { id, name };
}

const HOLDS = new Map<string, number>();

const FOLLOWED: ReadonlySet<ProposalAnalysis | null> = new Set(["started", "optimising", "ready"]);

const HAND_OVER_WINDOW_MS = 120_000;

const STORE = toolCallStore<ChatProposal>({
  key: "simon.analysis.chatProposal",
  narrow: narrowEntry,
  stage: (entry) => (entry.handedOver ? "done" : "awaiting"),
});
